// Browser-side notification helpers: favicon unread badge + audio ping.
//
// Favicon badge: redraws the favicon to overlay a small red bubble with the
// unread count. Falls back to the original favicon when count is zero.
//
// Audio ping: synthesizes a short two-tone beep via WebAudio (no asset needed).

let originalFaviconHref: string | null = null;
let lastBadgeCount = -1;

function ensureLink(): HTMLLinkElement {
  let link = document.querySelector<HTMLLinkElement>("link[rel*='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  if (originalFaviconHref === null) originalFaviconHref = link.href || '';
  return link;
}

export function setFaviconBadge(count: number): void {
  if (count === lastBadgeCount) return;
  lastBadgeCount = count;
  const link = ensureLink();
  if (count <= 0) {
    if (originalFaviconHref) link.href = originalFaviconHref;
    return;
  }
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  // Background — flat brand circle so the badge is recognizable even tiny.
  ctx.fillStyle = '#2563eb';
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
  ctx.fill();
  // White "C" mark
  ctx.fillStyle = 'white';
  ctx.font = 'bold 34px -apple-system, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('C', size / 2, size / 2 + 1);
  // Red unread bubble in top-right
  const r = 22;
  ctx.fillStyle = '#dc2626';
  ctx.beginPath();
  ctx.arc(size - r + 2, r - 2, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'white';
  const label = count > 9 ? '9+' : String(count);
  ctx.font = `bold ${count > 9 ? 22 : 28}px -apple-system, sans-serif`;
  ctx.fillText(label, size - r + 2, r);
  link.href = canvas.toDataURL('image/png');
}

let audioCtx: AudioContext | null = null;

export function playNotificationPing(): void {
  // Throttle: don't play more than once per 1.5s.
  const now = Date.now();
  if ((playNotificationPing as unknown as { last?: number }).last &&
      now - ((playNotificationPing as unknown as { last?: number }).last as number) < 1500) {
    return;
  }
  (playNotificationPing as unknown as { last?: number }).last = now;
  try {
    type AudioWin = Window & { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
    const Ctor = (window as unknown as AudioWin).AudioContext || (window as unknown as AudioWin).webkitAudioContext;
    if (!Ctor) return;
    if (!audioCtx) audioCtx = new Ctor();
    const ctx = audioCtx;
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      void ctx.resume();
    }
    const start = ctx.currentTime;
    const tones: Array<{ f: number; t: number; dur: number }> = [
      { f: 880, t: 0, dur: 0.12 },
      { f: 1320, t: 0.13, dur: 0.18 },
    ];
    for (const tone of tones) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = tone.f;
      gain.gain.value = 0.0001;
      gain.gain.exponentialRampToValueAtTime(0.18, start + tone.t + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + tone.t + tone.dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start + tone.t);
      osc.stop(start + tone.t + tone.dur + 0.02);
    }
  } catch {
    /* ignore */
  }
}
